import ReceiptLayout from '@/components/MedicalReceipt/ReceiptLayout';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import TicketExampleImage from '../../assets/uploadTicket/ticket.png';
import UploadIcon from '../../assets/uploadTicket/icon_upload.png';
import TicketUploadModal from '@/components/MedicalUploadTicket/TicketUploadModal';
import { useHistory } from 'react-router-dom';
import { useMutation } from 'react-query';
import { uploadTicket } from '@/api';
import { useSelector } from 'react-redux';
import { IGlobalState } from '@/store';
import { Spin } from '@/animation';
import { Viewer } from '@react-pdf-viewer/core';
import '@react-pdf-viewer/core/lib/styles/index.css';
import { convertHEICToJPEG, resizeFile } from '@repo/utils';

function MedicalUploadTicket() {
  const history = useHistory();
  const { passportNumber } = useSelector((state) => state) as IGlobalState;
  const { mutate: mutateUploadTicket } = useMutation(uploadTicket);
  const ticketInput = useRef<HTMLInputElement>(null);
  const [ticketFile, setTicketFile] = useState<any>(null);
  const [ticketImage, setTicketImage] = useState<any>(null);
  const [resultStatus, setResultStatus] = useState<'success' | 'fail' | null>(
    null,
  );
  const [isSending, setIsSending] = useState(false);

  const title = ticketImage
    ? '항공권을 확인해주세요.'
    : '항공권을 업로드 해주세요.';

  const description = ticketImage ? (
    <>
      정보들이 모두 보이도록 촬영 되었는지 확인하신 후
      <br />
      출국 인증하기 버튼을 클릭해주세요.
    </>
  ) : (
    <>
      하단 사항이 나오도록 항공권을 촬영하여 업로드해주세요.
      <br />
      지류 항공권, E-티켓의 캡처본 모두 업로드 가능합니다.
    </>
  );

  const uploadLabel = ticketImage ? '다시 올리기' : '항공권 업로드';

  const parseTicketImage = (file: File) => {
    return new Promise<string>((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.readAsDataURL(file);
    });
  };

  const isValidFileType = (type: string) =>
    ['pdf', 'image'].some((format) => type.includes(format));

  const onUploadTicketImage = async () => {
    if (ticketInput.current?.files) {
      const file = ticketInput.current.files[0];
      const { type } = file;
      if (isValidFileType(type)) {
        const imageFile =
          type === 'image/heic' ? await convertHEICToJPEG(file) : file;
        setTicketFile(imageFile);
        const image = await parseTicketImage(imageFile);
        setTicketImage(image);
      } else {
        alert('항공권 파일은 이미지 또는 PDF 형식만 업로드 가능합니다.');
      }
    }
  };

  const onSaveTicket = async () => {
    if (passportNumber && ticketFile) {
      const passportNumberBlob = new Blob(
        [JSON.stringify({ passportNumber })],
        {
          type: 'application/json',
        },
      );

      const resizedTicketFile = await resizeFile(ticketFile);
      const ticketUploadData = new FormData();
      ticketUploadData.append('request', passportNumberBlob);
      ticketUploadData.append('file', resizedTicketFile);

      setIsSending(true);
      mutateUploadTicket(ticketUploadData, {
        onSuccess: () => {
          setResultStatus('success');
          setIsSending(false);
        },
        onError: () => {
          setResultStatus('fail');
          setIsSending(false);
        },
      });
    }
  };

  const handleCloseModal = () => {
    if (resultStatus === 'success') {
      history.push('/home');
    }
    setResultStatus(null);
  };

  useEffect(() => {
    const handlePopstate = () => {
      setTicketImage(null);
    };
    window.addEventListener('popstate', handlePopstate);
    return () => {
      window.addEventListener('popstate', handlePopstate);
    };
  }, []);

  return (
    <>
      <ReceiptLayout>
        <Container>
          <Header>
            <Title>{title}</Title>
            <Description>{description}</Description>
          </Header>
          {ticketImage ? (
            <TicketSection isPDF={ticketFile?.type.includes('pdf')}>
              {ticketFile?.type.includes('pdf') ? (
                <PdfView fileUrl={ticketImage} defaultScale='PageWidth' />
              ) : (
                <img src={ticketImage} alt='' />
              )}
            </TicketSection>
          ) : (
            <TicketExampleImg src={TicketExampleImage} alt='' />
          )}
          <Bottom>
            <Caution>
              <CautionTitle>
                하단의 사항들이 모두 포함되도록 촬영해주세요.
              </CautionTitle>
              <CautionList>
                <CautionItem>고객명 (Passenger Name)</CautionItem>
                <CautionItem>항공편명, 시간 (Flight No, Date)</CautionItem>
                <CautionItem>출발지, 도착지 (From, To)</CautionItem>
              </CautionList>
            </Caution>
            <Buttons>
              <UploadButton htmlFor='ticket-input' reupload={!!ticketImage}>
                {!ticketImage && (
                  <img src={UploadIcon} alt='' width={24} height={24} />
                )}
                {uploadLabel}
                <TicketInput
                  id='ticket-input'
                  type='file'
                  accept='.heic, image/*, application/pdf'
                  onChange={onUploadTicketImage}
                  ref={ticketInput}
                />
              </UploadButton>
              <DepartureButton
                onClick={onSaveTicket}
                disabled={!ticketImage || isSending}
              >
                {isSending ? <LoadingSpinner /> : '출국 인증하기'}
              </DepartureButton>
            </Buttons>
          </Bottom>
        </Container>
      </ReceiptLayout>
      {resultStatus && (
        <TicketUploadModal
          onClose={handleCloseModal}
          isFail={resultStatus === 'fail'}
        />
      )}
    </>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: ${() => (window.innerHeight < 130 ? 'calc(100vh - 130px)' : '100%')};
`;

const Header = styled.header`
  width: 100%;
  padding: 20px 0;
`;

const Title = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #3a3b3e;
  margin-bottom: 12px;
`;

const Description = styled.p`
  font-size: 14px;
  font-weight: 400;
  line-height: 21px;
  color: #80848a;
`;

const Caution = styled.section`
  width: 100%;
  padding: 12px 16px;
  border-radius: 12px;
  background-color: #f5f6f7;
  color: #80848a;
  line-height: 20px;
  margin-bottom: 24px;
`;

const CautionTitle = styled.strong`
  font-size: 13px;
  font-weight: 600;
`;

const CautionList = styled.ul``;

const CautionItem = styled.li`
  font-size: 12px;
  font-weight: 500;

  &:before {
    content: '·';
    margin: 6px;
  }
`;

const UploadButton = styled.label<{ reupload: boolean }>`
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 52px;
  background-color: ${({ reupload }) => (reupload ? '#F4F8FF' : '#246cf6')};
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  color: ${({ reupload }) => (reupload ? '#246CF6' : '#ffffff')};
`;

const TicketInput = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

const DepartureButton = styled.button<{ disabled: boolean }>`
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 52px;
  background-color: ${({ disabled }) => (disabled ? '#E5E6E8' : '#246cf6')};
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  color: #ffffff;
`;

const Buttons = styled.footer`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 20px;
`;

const Bottom = styled.div`
  width: 100%;
`;

const TicketSection = styled.div<{ isPDF: boolean }>`
  width: 100%;
  aspect-ratio: 4/3;
  background-color: #f5f6f7;
  border-radius: 12px;
  border: 1px dashed #a7a7a7;
  margin-bottom: 20px;
  overflow-x: hidden;
  overflow-y: ${({ isPDF }) => (isPDF ? 'auto' : 'hidden')};

  & > img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const TicketExampleImg = styled.img`
  width: 100%;
  padding: 0 44px;
`;

const LoadingSpinner = styled.div`
  animation: ${Spin} 600ms linear infinite;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  border: 6px solid #e9f0fe;
  border-top: 6px solid #246cf6;
`;

const PdfView = styled(Viewer)`
  * {
    box-shadow: 0 !important;
  }
`;

export default MedicalUploadTicket;
